import React, { useEffect, useState } from 'react';
import { Card, message, Row, Col, Descriptions, Divider, Typography, Spin, Tag, Input, Button } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate } from 'react-router-dom';
import { theme } from '../../theme';
import { StyledCard } from '../common';

const { Title, Text } = Typography;

interface Rollo {
  id: number;
  rollo_identificacion: string;
  balanza: number;
  id_empresa: number;
  id_sucursal: number;
  tipo?: string; // Campo opcional para indicar el tipo de generación
  alta: number;
}

interface BalanzaData {
  id: number;
  numero_ticket: number;
  fecha_ticket: string;
  cliente_nombre: string;
  productor_nombre: string;
  transportista_nombre: string;
  chofer_nombre: string;
  camion_patente: string;
  producto_nombre: string;
  cosecha_nombre: string;
  variedad_semilla_nombre: string;
  establecimiento_nombre: string;
  nlote_nombre: string;
  presentacion_nombre: string;
  dtv: string;
  ingreso_egreso: string;
  compra_servicio: string;
  kilos_primera: number;
  kilos_segunda: number;
  humedad: number;
  cierre: string;
  numero_remito: string;
  ubicacion: string;
  cantidad_rollos: number;
  puntaje_presentacion: string;
  fecha_hora_primera: string;
  fecha_hora_segunda: string;
  tarifa: number;
  id_sucursal: number;
  id_empresa: number;
  alta: number;
}

interface ApiResponse {
  status: string;
  data: BalanzaData[];
  rollos: Rollo[];
}

const AltaRollos: React.FC = () => {
  const [balanzaData, setBalanzaData] = useState<BalanzaData | null>(null);
  const [rollos, setRollos] = useState<Rollo[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoNumericoLoading, setAutoNumericoLoading] = useState(false);
  const [grabarTodoLoading, setGrabarTodoLoading] = useState(false);
  const [nuevoRollo, setNuevoRollo] = useState('');
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useAuth0();
  const { registroId } = useParams<{ registroId: string }>();
  const navigate = useNavigate();
  
  const id_empresa = user?.id_empresa;
  const id_sucursal = user?.id_sucursal;

  useEffect(() => {
    const cargarDatosBalanza = async () => {
      if (!registroId || !id_empresa || !id_sucursal) {
        message.error('Faltan parámetros requeridos');
        return;
      }

      setLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_BALANZA_URL}/registro_balanza_rollos/${id_empresa}/${id_sucursal}/${registroId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Error al cargar datos: ${response.status}`);
        }

        const data: ApiResponse = await response.json();
        
        if (data.status === 'success' && data.data.length > 0) {
          setBalanzaData(data.data[0]);
          setRollos(data.rollos || []);
        } else {
          message.error('No se encontraron datos para este registro');
        }
      } catch (error) {
        console.error('Error al cargar datos de balanza:', error);
        message.error('Error al cargar los datos de balanza');
      } finally {
        setLoading(false);
      }
    };

    cargarDatosBalanza();
  }, [registroId, id_empresa, id_sucursal, getAccessTokenSilently]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-AR');
  };

  const formatDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return '-';
    return new Date(dateTimeString).toLocaleString('es-AR');
  };

  const getEstadoColor = (cierre: string) => {
    return cierre === '1' ? 'green' : 'orange';
  };

  const getEstadoText = (cierre: string) => {
    return cierre === '1' ? 'Cerrado' : 'Abierto';
  };

  const handleEliminarRollo = (index: number) => {
    const nuevosRollos = rollos.filter((_, i) => i !== index);
    setRollos(nuevosRollos);
  };

  const handleActualizarRollo = (index: number, valor: string) => {
    const nuevosRollos = [...rollos];
    nuevosRollos[index] = { ...nuevosRollos[index], rollo_identificacion: valor };
    setRollos(nuevosRollos);
  };

  const handleAgregarRollo = () => {
    if (nuevoRollo.trim()) {
      const nuevoRolloObj: Rollo = {
        id: Date.now(), // ID temporal
        rollo_identificacion: nuevoRollo.trim(),
        balanza: balanzaData?.id || 0,
        id_empresa: id_empresa || 0,
        id_sucursal: id_sucursal || 0,
        tipo: '', // Tipo vacío para rollos agregados manualmente
        alta: 1
      };
      setRollos([...rollos, nuevoRolloObj]);
      setNuevoRollo('');
    }
  };

    const handleAutoNumerico = async () => {
    if (!balanzaData) {
      message.error('No hay datos de balanza disponibles');
      return;
    }

    const cantidadRollos = balanzaData.cantidad_rollos;
    if (!cantidadRollos || cantidadRollos <= 0) {
      message.error('La cantidad de rollos debe ser mayor a 0');
      return;
    }

    console.log('Iniciando AutoNumérico, estableciendo loading a true');
    setAutoNumericoLoading(true);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_BALANZA_URL}/numero_rollo/${id_empresa}/${id_sucursal}/${cantidadRollos}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Error al obtener números automáticos: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        const numerosDisponibles = data.numeros_disponibles;
        
                 // Generar automáticamente los rollos según la cantidad especificada
         const nuevosRollos: Rollo[] = [];
         for (let i = 0; i < numerosDisponibles.length; i++) {
           const nuevoRolloObj: Rollo = {
             id: Date.now() + i, // ID temporal único
             rollo_identificacion: numerosDisponibles[i].toString(),
             balanza: balanzaData?.id || 0,
             id_empresa: id_empresa || 0,
             id_sucursal: id_sucursal || 0,
             tipo: 'A', // Tipo 'A' para rollos generados automáticamente
             alta: 1
           };
           nuevosRollos.push(nuevoRolloObj);
         }
        
                // Reemplazar los rollos existentes con los nuevos (no agregar a la lista existente)
         setRollos(nuevosRollos);
         
         // Limpiar el campo de nuevo rollo para evitar confusión
         setNuevoRollo('');
        
        message.success(`Generados ${cantidadRollos} rollos automáticos desde el número ${numerosDisponibles[0]}`);
      } else {
        message.error('Error al generar números automáticos');
      }
    } catch (error) {
      console.error('Error al obtener números automáticos:', error);
      message.error('Error al generar números automáticos');
         } finally {
       console.log('Finalizando AutoNumérico, estableciendo loading a false');
       setAutoNumericoLoading(false);
     }
   };

  const handleGrabarTodo = async () => {
    if (rollos.length === 0) {
      message.error('No hay rollos para grabar');
      return;
    }

    setGrabarTodoLoading(true);
    try {
      const token = await getAccessTokenSilently();
      
      // Preparar los datos de los rollos para enviar al backend
      const rollosParaGrabar = rollos.map(rollo => ({
        rollo_identificacion: rollo.rollo_identificacion,
        balanza: rollo.balanza,
        id_empresa: rollo.id_empresa,
        id_sucursal: rollo.id_sucursal,
        alta: rollo.alta
      }));

      const response = await fetch(
        `${process.env.REACT_APP_BALANZA_URL}/crear_rollos/${id_empresa}/${id_sucursal}/`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(rollosParaGrabar)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const backendMsg = errorData?.message || errorData?.error || `HTTP ${response.status}`;
        throw new Error(backendMsg);
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        message.success(data.message);
        // Limpiar la lista de rollos después de grabar exitosamente
        setRollos([]);
        // Redirigir a la página de listar pesajes después de un breve delay
        setTimeout(() => {
          navigate('/listar_pesaje');
        }, 1500); // 1.5 segundos de delay para que el usuario vea el mensaje de éxito
      } else if (data.status === 'partial_success') {
        message.warning(data.message);
        if (data.errores && data.errores.length > 0) {
          console.error('Errores al grabar algunos rollos:', data.errores);
        }
      } else {
        message.error(data.message || 'Error al grabar rollos');
      }
    } catch (error) {
      console.error('Error al grabar rollos:', error);
      const errMsg = error instanceof Error ? error.message : 'Error al grabar los rollos';
      message.error(errMsg, 8);
    } finally {
      setGrabarTodoLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Cargando datos de balanza...</div>
      </div>
    );
  }

  if (!balanzaData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text type="secondary">No se encontraron datos para este registro</Text>
      </div>
    );
  }

  // Debug: mostrar el estado del loading
  console.log('Estado autoNumericoLoading:', autoNumericoLoading);

    return (
    <div style={{ padding: '4px' }}>
      <StyledCard
        title={<span style={{ fontSize: '12px', lineHeight: '1.1' }}>MÓDULOS/ROLLOS</span>}
        bodyStyle={{ padding: '6px' }}
        headStyle={{ padding: '4px 12px', minHeight: 'auto' }}
      >

      {/* Datos del Ticket de Balanza - Reorganizados en 2 cuadrantes */}
      <Row gutter={[8, 4]} style={{ marginBottom: '6px' }}>
        {/* Cuadrante Izquierdo - 50% */}
        <Col xs={24} lg={12} style={{ display: 'flex' }}>
          <StyledCard
            title={
              <div style={{ textAlign: 'center', color: 'white', fontSize: '11px', lineHeight: '1.1' }}>
                Ticket N° {balanzaData.numero_ticket}
              </div>
            }
            bodyStyle={{ padding: '6px', flex: 1, display: 'flex', flexDirection: 'column' }}
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            headStyle={{ padding: '4px 8px', minHeight: 'auto' }}
          >
            <Descriptions 
              column={3} 
              size="small" 
              bordered={false}
              style={{ flex: 1 }}
              labelStyle={{ fontWeight: 600, color: theme.colors.textPrimary, fontSize: '10px', paddingBottom: '1px', paddingTop: '1px' }}
              contentStyle={{ color: theme.colors.textPrimary, fontWeight: 500, fontSize: '10px', paddingBottom: '1px', paddingTop: '1px' }}
            >
              <Descriptions.Item label="Ticket">
                <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>{balanzaData.numero_ticket}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Fecha">
                <span style={{ color: theme.colors.textPrimary }}>{formatDate(balanzaData.fecha_ticket)}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Producto">
                <span style={{ color: theme.colors.textPrimary }}>{balanzaData.producto_nombre || '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Cosecha">
                <span style={{ color: theme.colors.textSecondary }}>{balanzaData.cosecha_nombre || '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Variedad">
                <span style={{ color: theme.colors.textSecondary }}>{balanzaData.variedad_semilla_nombre || '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Presentación">
                <span style={{ color: theme.colors.textSecondary }}>{balanzaData.presentacion_nombre || '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="1ra Pesada">
                <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>{balanzaData.kilos_primera} kg</span>
              </Descriptions.Item>
              <Descriptions.Item label="2da Pesada">
                <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>{balanzaData.kilos_segunda} kg</span>
              </Descriptions.Item>
              <Descriptions.Item label="Peso Neto">
                <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>{Math.abs(balanzaData.kilos_primera - balanzaData.kilos_segunda)} kg</span>
              </Descriptions.Item>
              <Descriptions.Item label="Humedad">
                <span style={{ color: theme.colors.textPrimary }}>{balanzaData.humedad}%</span>
              </Descriptions.Item>
              <Descriptions.Item label="Rollos">
                <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>{balanzaData.cantidad_rollos}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Cliente">
                <span style={{ color: theme.colors.textPrimary }}>{balanzaData.cliente_nombre || '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Productor">
                <span style={{ color: theme.colors.textSecondary }}>{balanzaData.productor_nombre || '-'}</span>
              </Descriptions.Item>
            </Descriptions>
          </StyledCard>
        </Col>

        {/* Cuadrante Derecho - 50% */}
        <Col xs={24} lg={12} style={{ display: 'flex' }}>
          <StyledCard
            title={<span style={{ fontSize: '11px', lineHeight: '1.1' }}>Detalles Adicionales</span>}
            bodyStyle={{ padding: '6px', flex: 1, display: 'flex', flexDirection: 'column' }}
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            headStyle={{ padding: '4px 8px', minHeight: 'auto' }}
          >
            <Descriptions 
              column={3} 
              size="small" 
              bordered={false}
              style={{ flex: 1 }}
              labelStyle={{ fontWeight: 600, color: theme.colors.textPrimary, fontSize: '10px', paddingBottom: '1px', paddingTop: '1px' }}
              contentStyle={{ color: theme.colors.textPrimary, fontWeight: 500, fontSize: '10px', paddingBottom: '1px', paddingTop: '1px' }}
            >
              <Descriptions.Item label="Transportista">
                <span style={{ color: theme.colors.textPrimary }}>{balanzaData.transportista_nombre || '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Chofer">
                <span style={{ color: theme.colors.textSecondary }}>{balanzaData.chofer_nombre || '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Camión">
                <span style={{ color: theme.colors.textSecondary }}>{balanzaData.camion_patente || '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Establecimiento">
                <span style={{ color: theme.colors.textSecondary }}>{balanzaData.establecimiento_nombre || '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Lote">
                <span style={{ color: theme.colors.textSecondary }}>{balanzaData.nlote_nombre || '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Ubicación">
                <span style={{ color: theme.colors.textSecondary }}>{balanzaData.ubicacion || '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="DTV">
                <span style={{ color: theme.colors.textSecondary }}>{balanzaData.dtv || '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Remito">
                <span style={{ color: theme.colors.textSecondary }}>{balanzaData.numero_remito || '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Movimiento">
                <span style={{ 
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: '8px',
                  fontSize: '10px',
                  fontWeight: 600,
                  backgroundColor: balanzaData.ingreso_egreso === 'I' ? '#d4edda' : '#f8d7da',
                  color: balanzaData.ingreso_egreso === 'I' ? '#155724' : '#721c24'
                }}>
                  {balanzaData.ingreso_egreso === 'I' ? 'Ingreso' : 'Salida'}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Motivo">
                <span style={{ color: theme.colors.textSecondary }}>{balanzaData.compra_servicio === 'C' ? 'Compra' : 'Servicio'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="1ra Pesada Hora">
                <span style={{ color: theme.colors.textSecondary }}>{formatDateTime(balanzaData.fecha_hora_primera)}</span>
              </Descriptions.Item>
              <Descriptions.Item label="2da Pesada Hora">
                <span style={{ color: theme.colors.textSecondary }}>{formatDateTime(balanzaData.fecha_hora_segunda)}</span>
              </Descriptions.Item>
            </Descriptions>
          </StyledCard>
        </Col>
      </Row>

      {/* Botones de Acción */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '4px',
        padding: '2px'
      }}>
        <Button 
          type="primary" 
          size="small"
          onClick={handleAutoNumerico}
          loading={autoNumericoLoading}
          disabled={autoNumericoLoading}
          style={{ 
            marginRight: '8px',
            minWidth: '100px',
            height: '28px',
            fontSize: '12px',
            backgroundColor: theme.colors.primary,
            borderColor: theme.colors.primary
          }}
        >
          AutoNumérico
        </Button>
        <Button 
          type="primary" 
          size="small"
          onClick={handleGrabarTodo}
          loading={grabarTodoLoading}
          disabled={autoNumericoLoading === true || rollos.length === 0 || grabarTodoLoading}
          style={{ 
            minWidth: '100px',
            height: '28px',
            fontSize: '12px',
            backgroundColor: theme.colors.primary,
            borderColor: theme.colors.primary
          }}
        >
          GrabarTodo
        </Button>
      </div>

      {/* Lista de Rollos */}
      <StyledCard
        title={<span style={{ fontSize: '11px', lineHeight: '1.1' }}>Gestión de Rollos</span>}
        bodyStyle={{ padding: '6px' }}
        headStyle={{ padding: '4px 8px', minHeight: 'auto' }}
      >
         
        {/* Rollos existentes */}
        {rollos.map((rollo, index) => (
          <Row key={rollo.id} style={{ marginBottom: '4px', alignItems: 'center', justifyContent: 'center' }}>
            <Col span={10}>
              <Input
                value={rollo.rollo_identificacion}
                onChange={(e) => handleActualizarRollo(index, e.target.value)}
                placeholder="Identificación del rollo"
                size="small"
                maxLength={24}
                disabled={true}
                style={{ 
                  textAlign: 'center',
                  fontFamily: 'monospace',
                  fontSize: '11px',
                  letterSpacing: '0.3px',
                  width: '100%',
                  maxWidth: '200px',
                  opacity: 0.6,
                  height: '26px'
                }}
              />
            </Col>
            <Col span={6} style={{ paddingLeft: '4px' }}>
              <Button 
                type="primary" 
                danger 
                size="small"
                onClick={() => handleEliminarRollo(index)}
                disabled={autoNumericoLoading === true || grabarTodoLoading}
                style={{ height: '26px', fontSize: '11px' }}
              >
                Eliminar
              </Button>
            </Col>
          </Row>
        ))}

        {/* Input para agregar nuevo rollo */}
        <Row style={{ marginTop: '4px', alignItems: 'center', justifyContent: 'center' }}>
          <Col span={10}>
            <Input
              value={nuevoRollo}
              onChange={(e) => setNuevoRollo(e.target.value)}
              placeholder="Ingrese identificación del rollo"
              size="small"
              maxLength={24}
              onPressEnter={handleAgregarRollo}
              disabled={autoNumericoLoading === true || grabarTodoLoading}
              style={{ 
                textAlign: 'center',
                fontFamily: 'monospace',
                fontSize: '11px',
                letterSpacing: '0.3px',
                width: '100%',
                maxWidth: '200px',
                opacity: (autoNumericoLoading || grabarTodoLoading) ? 0.6 : 1,
                height: '26px'
              }}
            />
          </Col>
          <Col span={6} style={{ paddingLeft: '4px' }}>
            <Button 
              type="primary" 
              size="small"
              onClick={handleAgregarRollo}
              disabled={!nuevoRollo.trim() || autoNumericoLoading === true || grabarTodoLoading}
              style={{
                backgroundColor: theme.colors.primary,
                borderColor: theme.colors.primary,
                height: '26px',
                fontSize: '11px'
              }}
            >
              Agregar
            </Button>
          </Col>
        </Row>

        {rollos.length === 0 && (
          <div style={{ textAlign: 'center', padding: '6px' }}>
            <Text type="secondary" style={{ color: theme.colors.textSecondary, fontSize: '10px' }}>
              No hay rollos registrados para este ticket
            </Text>
          </div>
        )}
      </StyledCard>
    </StyledCard>
    </div>
  );
};

export default AltaRollos;
